import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button, Divider, Input, InputNumber, Modal, Select, Space, Table, Tag, message,
} from 'antd';
import { CheckOutlined, CloseOutlined, MinusSquareOutlined } from '@ant-design/icons';
import { parseISO, format } from 'date-fns';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';

const APPROVED_META = {
  pending: { color: 'orange', label: 'Ожидает' },
  approved: { color: 'green', label: 'Подтверждён' },
  rejected: { color: 'red', label: 'Отклонён' },
};

function displayUnit(record) {
  if (!record) return '';
  return record.material_unit || record.unit || record.category_unit || '';
}

function formatQty(qty, unit) {
  if (qty === undefined || qty === null || qty === '') return '—';
  const s = String(Math.trunc(Number(qty)));
  return unit ? `${s} ${unit}` : s;
}

function parsePositiveInteger(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) return null;
  return n;
}

function availableQty(record) {
  const q = record?.quantity;
  if (q === undefined || q === null || q === '') return 0;
  return Math.floor(Number(q));
}

function normalizeApproved(value) {
  if (value === 0 || value === '0') return 'pending';
  if (value === 1 || value === '1') return 'approved';
  if (value === 2 || value === '2') return 'rejected';
  return value;
}

const InventoryStock = ({ currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pendingMoves, setPendingMoves] = useState([]);

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const taskFetchSeq = useRef(0);
  const taskSearchTimer = useRef(null);

  const [consumeItem, setConsumeItem] = useState(null);
  const [consumeTask, setConsumeTask] = useState(null);
  const [consumeQty, setConsumeQty] = useState(null);
  const [consumeModalOpen, setConsumeModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [returnItem, setReturnItem] = useState(null);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnQty, setReturnQty] = useState(null);
  const [autoWarehouseMaterialId, setAutoWarehouseMaterialId] = useState(null);
  const [returnWarehouseLabel, setReturnWarehouseLabel] = useState('');
  const [manualWarehouseId, setManualWarehouseId] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [manualWarehousePick, setManualWarehousePick] = useState(false);

  const loadItems = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    return Rest.get('/api/v1/inventories/my.json')
      .then((r) => setItems(r.data.inventories || []))
      .catch(() => message.error('Не удалось загрузить инвентарь'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  const loadPending = useCallback(() => (
    Rest.get('/api/v1/material_moves/pending.json')
      .then((r) => setPendingMoves(r.data.material_moves || []))
      .catch(() => {})
  ), []);

  const refreshAll = useCallback((opts = {}) => {
    loadItems(opts);
    loadPending();
  }, [loadItems, loadPending]);

  useEffect(() => {
    if (!currentUser?.id) return;
    refreshAll();
  }, [currentUser?.id, refreshAll]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshAll({ silent: true });
    };
    const onFocus = () => refreshAll({ silent: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const timer = setInterval(() => refreshAll({ silent: true }), 30_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(timer);
    };
  }, [refreshAll]);

  const handleApprove = (moveId) => {
    Rest.put(`/api/v1/material_moves/${moveId}/approve.json`)
      .then(() => {
        message.success('Принято');
        refreshAll();
      })
      .catch((err) => message.error(err.response?.data?.error || 'Ошибка'));
  };

  const handleReject = (moveId) => {
    Modal.confirm({
      title: 'Отклонить перемещение?',
      okText: 'Отклонить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: () => Rest.put(`/api/v1/material_moves/${moveId}/reject.json`)
        .then(() => {
          message.success('Отклонено');
          refreshAll();
        })
        .catch((err) => message.error(err.response?.data?.error || 'Ошибка')),
    });
  };

  const loadTasks = (queryRaw) => {
    const trimmed = (queryRaw ?? '').trim();
    if (trimmed && !/^\d+$/.test(trimmed)) {
      taskFetchSeq.current += 1;
      setTasks([]);
      setTasksLoading(false);
      return;
    }
    const params = { page: 1, per: 50 };
    if (trimmed) params.search = { number: parseInt(trimmed, 10) };

    const seq = ++taskFetchSeq.current;
    setTasksLoading(true);
    Rest.get('/api/v1/requests.json', { params })
      .then((r) => {
        if (seq !== taskFetchSeq.current) return;
        setTasks(r.data.requests || []);
      })
      .catch(() => {
        if (seq !== taskFetchSeq.current) return;
        setTasks([]);
      })
      .finally(() => {
        if (seq !== taskFetchSeq.current) return;
        setTasksLoading(false);
      });
  };

  const openConsumeFlow = (item) => {
    if (availableQty(item) <= 0) {
      message.error('Нет материала в наличии');
      return;
    }
    setConsumeItem(item);
    setConsumeTask(null);
    setConsumeQty(null);
    setTaskSearch('');
    setTaskModalOpen(true);
    loadTasks('');
  };

  const handleTaskPicked = (task) => {
    setConsumeTask(task);
    setTaskModalOpen(false);
    setConsumeModalOpen(true);
  };

  const handleConsume = () => {
    const q = parsePositiveInteger(consumeQty);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }
    if (q > availableQty(consumeItem)) {
      message.error(`Недостаточно (доступно: ${formatQty(consumeItem.quantity, displayUnit(consumeItem))})`);
      return;
    }

    setSubmitting(true);
    Rest.post('/api/v1/material_moves.json', {
      material_move: {
        source_type: 'Inventory',
        source_user_id: currentUser.id,
        material_id: consumeItem.material_id,
        destination_type: 'Request',
        destination_id: consumeTask.id,
        quantity: q,
      },
    })
      .then(() => {
        message.success('Материал списан на задачу');
        setConsumeModalOpen(false);
        setConsumeItem(null);
        setConsumeTask(null);
        setConsumeQty(null);
        refreshAll();
      })
      .catch((err) => {
        message.error(err.response?.data?.errors?.join(', ') || 'Ошибка списания');
      })
      .finally(() => setSubmitting(false));
  };

  const openReturnModal = (item) => {
    setReturnItem(item);
    setReturnQty(null);
    setAutoWarehouseMaterialId(null);
    setReturnWarehouseLabel('');
    setManualWarehouseId(null);
    setManualWarehousePick(false);
    setReturnModalOpen(true);

    Rest.get('/api/v1/warehouses.json')
      .then((r) => setWarehouses(r.data.warehouses || []))
      .catch(() => setWarehouses([]));

    Rest.get('/api/v1/material_moves.json', {
      params: {
        per: 1,
        page: 1,
        search: {
          material_id: item.material_id,
          destination_type: 'Inventory',
          destination_id: item.id,
          source_type: 'WarehouseMaterial',
          approved: 'approved',
        },
      },
    })
      .then((r) => {
        const move = (r.data.material_moves || [])[0];
        if (!move?.source_id) return;
        setAutoWarehouseMaterialId(move.source_id);
        setReturnWarehouseLabel(move.source_label || 'Склад');
      })
      .catch(() => {});
  };

  const handleReturn = () => {
    const q = parsePositiveInteger(returnQty);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }
    if (q > availableQty(returnItem)) {
      message.error(`Недостаточно (доступно: ${formatQty(returnItem.quantity, displayUnit(returnItem))})`);
      return;
    }

    const resolveDestination = () => {
      if (!manualWarehousePick && autoWarehouseMaterialId) {
        return Promise.resolve(autoWarehouseMaterialId);
      }
      const warehouseId = manualWarehouseId;
      if (!warehouseId) {
        message.error('Не удалось определить склад. Выберите вручную.');
        setManualWarehousePick(true);
        return Promise.resolve(null);
      }
      return Rest.post('/api/v1/warehouse_materials/ensure.json', {
        warehouse_id: warehouseId,
        material_id: returnItem.material_id,
      }).then((resp) => {
        const wmId = resp.data?.warehouse_material?.id;
        if (!wmId) throw new Error('Не удалось подготовить позицию склада');
        return wmId;
      });
    };

    setSubmitting(true);
    resolveDestination()
      .then((destinationId) => {
        if (!destinationId) {
          setSubmitting(false);
          return null;
        }
        return Rest.post('/api/v1/material_moves.json', {
          material_move: {
            source_type: 'Inventory',
            source_user_id: currentUser.id,
            material_id: returnItem.material_id,
            destination_type: 'WarehouseMaterial',
            destination_id: destinationId,
            quantity: q,
          },
        });
      })
      .then((resp) => {
        if (!resp) return;
        message.success('Возврат создан и ожидает подтверждения');
        setReturnModalOpen(false);
        setReturnItem(null);
        refreshAll();
      })
      .catch((err) => {
        message.error(err.response?.data?.errors?.join(', ') || err.message || 'Ошибка возврата');
      })
      .finally(() => setSubmitting(false));
  };

  const taskEmptyText = useMemo(() => {
    if (tasksLoading) return 'Загрузка…';
    const t = taskSearch.trim();
    if (t && !/^\d+$/.test(t)) return 'Введите только цифры номера задачи';
    if (t && /^\d+$/.test(t)) return 'Задачи с таким номером нет';
    return 'Нет задач';
  }, [taskSearch, tasksLoading]);

  const pendingColumns = [
    { title: 'Материал', dataIndex: 'material_name' },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '12%',
      render: (qty, record) => formatQty(qty, displayUnit(record)),
    },
    {
      title: 'Статус',
      dataIndex: 'approved',
      width: '10%',
      render: (s) => {
        const key = normalizeApproved(s);
        const meta = APPROVED_META[key] || { color: 'default', label: key };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: '15%',
      render: (d) => (d ? format(parseISO(d), 'dd.MM.yyyy HH:mm') : '—'),
    },
    {
      title: 'Действия',
      width: '20%',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record.id)}
          >
            Принять
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleReject(record.id)}
          >
            Отклонить
          </Button>
        </Space>
      ),
    },
  ];

  const inventoryColumns = [
    { title: 'Материал', dataIndex: 'material_name' },
    {
      title: 'Категория',
      dataIndex: 'category_name',
      render: (v) => v || '—',
    },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '12%',
      render: (qty, record) => formatQty(qty, displayUnit(record)),
    },
    {
      title: 'Действия',
      width: '16%',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            title="Списать на задачу"
            icon={<MinusSquareOutlined />}
            onClick={() => openConsumeFlow(record)}
          />
          <Button size="small" onClick={() => openReturnModal(record)}>
            Вернуть
          </Button>
        </Space>
      ),
    },
  ];

  const taskColumns = [
    {
      title: '№',
      dataIndex: 'id',
      width: 60,
      render: (id) => <strong>#{id}</strong>,
    },
    { title: 'Описание', dataIndex: 'description' },
    {
      title: 'Статус',
      dataIndex: ['request_status', 'name'],
      width: 120,
      render: (status) => <Tag>{status || '—'}</Tag>,
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: 140,
      render: (d) => (d ? format(parseISO(d), 'dd.MM.yyyy HH:mm') : '—'),
    },
  ];

  return (
    <Preloader loading={loading}>
      {pendingMoves.length > 0 && (
        <>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Ожидает приёмки</div>
          <Table
            rowKey="id"
            size="small"
            columns={pendingColumns}
            dataSource={pendingMoves}
            pagination={false}
          />
          <Divider />
        </>
      )}

      <Table
        rowKey="id"
        size="small"
        columns={inventoryColumns}
        dataSource={items}
        pagination={false}
        locale={{
          emptyText: 'Пусто. Остаток появится после перемещения материала со склада.',
        }}
      />

      <Modal
        title="Выберите задачу для списания"
        open={taskModalOpen}
        onCancel={() => {
          setTaskModalOpen(false);
          if (!consumeModalOpen) setConsumeItem(null);
        }}
        footer={null}
        width="70%"
        destroyOnClose
      >
        <Input
          placeholder="Номер задачи (например 63)…"
          value={taskSearch}
          onChange={(e) => {
            const { value } = e.target;
            setTaskSearch(value);
            if (taskSearchTimer.current) clearTimeout(taskSearchTimer.current);
            taskSearchTimer.current = setTimeout(() => loadTasks(value), 350);
          }}
          onPressEnter={(e) => loadTasks(e.target.value)}
          style={{ marginBottom: 16 }}
          allowClear
          onClear={() => {
            setTaskSearch('');
            loadTasks('');
          }}
        />
        <Table
          rowKey="id"
          size="small"
          columns={taskColumns}
          dataSource={tasks}
          loading={tasksLoading}
          pagination={false}
          locale={{ emptyText: taskEmptyText }}
          onRow={(record) => ({
            onClick: () => handleTaskPicked(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>

      <Modal
        title={`Списать: ${consumeItem?.material_name || ''}`}
        open={consumeModalOpen}
        onOk={handleConsume}
        onCancel={() => {
          setConsumeModalOpen(false);
          setConsumeItem(null);
          setConsumeTask(null);
          setConsumeQty(null);
        }}
        confirmLoading={submitting}
        okText="Списать"
        cancelText="Отмена"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
          <strong>Задача #{consumeTask?.id}:</strong> {consumeTask?.description || '—'}
        </div>
        <Button type="link" size="small" onClick={() => setTaskModalOpen(true)} style={{ paddingLeft: 0 }}>
          Выбрать другую задачу
        </Button>
        <p>
          Доступно: <strong>{formatQty(consumeItem?.quantity, displayUnit(consumeItem))}</strong>
        </p>
        <InputNumber
          min={1}
          precision={0}
          value={consumeQty}
          onChange={setConsumeQty}
          style={{ width: 160 }}
          placeholder="Количество"
        />
        {displayUnit(consumeItem) ? (
          <span style={{ marginLeft: 8, color: '#888' }}>{displayUnit(consumeItem)}</span>
        ) : null}
      </Modal>

      <Modal
        title={`Вернуть на склад: ${returnItem?.material_name || ''}`}
        open={returnModalOpen}
        onOk={handleReturn}
        onCancel={() => {
          setReturnModalOpen(false);
          setReturnItem(null);
        }}
        confirmLoading={submitting}
        okText="Создать возврат"
        cancelText="Отмена"
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          Склад:{' '}
          <strong>
            {returnWarehouseLabel || (manualWarehousePick ? 'выберите вручную' : 'определяем…')}
          </strong>
        </p>
        {manualWarehousePick && (
          <Select
            value={manualWarehouseId}
            onChange={setManualWarehouseId}
            placeholder="Выберите склад"
            style={{ width: '100%', marginBottom: 16 }}
            allowClear
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          />
        )}
        <p style={{ marginBottom: 8 }}>
          Доступно: <strong>{formatQty(returnItem?.quantity, displayUnit(returnItem))}</strong>
        </p>
        <InputNumber
          min={1}
          precision={0}
          value={returnQty}
          onChange={setReturnQty}
          style={{ width: '100%' }}
          placeholder="Количество"
        />
      </Modal>
    </Preloader>
  );
};

const mapStateToProps = (state) => ({ currentUser: state.user });

export default connect(mapStateToProps)(InventoryStock);
